import { useState } from "react";

/**
 * Inline SVG spend chart — same visual language as AdminAnalytics, but keyed on
 * the cost-event series buckets returned by /api/admin/spend/series.
 */
export default function SpendChart({ series, valueKey, label, accent, format, testId, footnote }) {
  const [hover, setHover] = useState(null);
  const width = 560;
  const height = 150;
  const pad = { top: 14, right: 10, bottom: 24, left: 52 };
  const chartW = width - pad.left - pad.right;
  const chartH = height - pad.top - pad.bottom;

  const rows = (series || []).filter(Boolean);
  if (rows.length === 0) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid={testId}>
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="h-[110px] grid place-items-center text-xs text-zinc-600 font-mono">
          No cost events in this range
        </div>
      </div>
    );
  }

  const values = rows.map((d) => Number(d[valueKey] || 0));
  const peak = Math.max(...values);
  const maxVal = peak || 1;
  const step = rows.length > 1 ? chartW / (rows.length - 1) : chartW;
  const points = rows.map((d, i) => ({
    x: pad.left + i * step,
    y: pad.top + chartH - (Number(d[valueKey] || 0) / maxVal) * chartH,
    v: Number(d[valueKey] || 0),
    bucket: d.bucket,
  }));
  const pathD = points.reduce(
    (acc, p, i) => acc + `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)} `,
    ""
  );
  const areaD =
    `${pathD} L${points[points.length - 1].x.toFixed(1)},${(pad.top + chartH).toFixed(1)} ` +
    `L${points[0].x.toFixed(1)},${(pad.top + chartH).toFixed(1)} Z`;
  const total = values.reduce((s, v) => s + v, 0);

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
          <div className="font-heading text-3xl font-black tracking-tighter text-white mt-1">
            {format(valueKey.includes("per_minute") ? peak : total)}
          </div>
          <div className="text-[11px] font-mono text-zinc-500 mt-1">
            {footnote || `${rows.length} bucket${rows.length === 1 ? "" : "s"}`}
          </div>
        </div>
        {hover && (
          <div className="text-right" data-testid={`${testId}-hover`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{hover.bucket}</div>
            <div className="text-sm text-white font-mono">{format(hover.v)}</div>
          </div>
        )}
      </div>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-auto"
        preserveAspectRatio="none"
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={`spendgrad-${testId}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.3" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, maxVal].map((t) => {
          const y = pad.top + chartH - (t / maxVal) * chartH;
          return (
            <g key={`t-${t}`}>
              <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#27272a" strokeDasharray="2 3" />
              <text
                x={pad.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-zinc-600"
                style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
              >
                {format(t)}
              </text>
            </g>
          );
        })}
        <path d={areaD} fill={`url(#spendgrad-${testId})`} />
        <path d={pathD} fill="none" stroke={accent} strokeWidth="1.5" />
        {points.map((p) => (
          <g key={p.bucket}>
            <circle cx={p.x} cy={p.y} r={hover?.bucket === p.bucket ? 3.5 : 2} fill={accent} opacity={0.9} />
            <rect
              x={p.x - step / 2}
              y={pad.top}
              width={step}
              height={chartH}
              fill="transparent"
              onMouseEnter={() => setHover(p)}
              style={{ cursor: "crosshair" }}
            />
          </g>
        ))}
        <text
          x={pad.left}
          y={height - 5}
          className="fill-zinc-600"
          style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {rows[0]?.bucket}
        </text>
        <text
          x={width - pad.right}
          y={height - 5}
          textAnchor="end"
          className="fill-zinc-600"
          style={{ font: "10px ui-monospace, SFMono-Regular, Menlo, monospace" }}
        >
          {rows[rows.length - 1]?.bucket}
        </text>
      </svg>
    </div>
  );
}
